import * as React from "react"

// ── Valid HTML/SVG attribute set ─────────────────────────────────────
// Comprehensive set of valid DOM attributes. Derived from React's internal
// DOM property list (same source @emotion/is-prop-valid uses). This avoids
// an external dependency and compresses well with gzip.

const VALID_HTML_PROPS = new Set([
  // HTML global attributes
  "abbr",
  "accept",
  "acceptCharset",
  "accessKey",
  "action",
  "allow",
  "allowFullScreen",
  "alt",
  "as",
  "async",
  "autoCapitalize",
  "autoComplete",
  "autoCorrect",
  "autoFocus",
  "autoPlay",
  "autoSave",
  "capture",
  "cellPadding",
  "cellSpacing",
  "challenge",
  "charSet",
  "checked",
  "cite",
  "classID",
  "className",
  "colSpan",
  "cols",
  "content",
  "contentEditable",
  "controls",
  "controlsList",
  "coords",
  "crossOrigin",
  "data",
  "dateTime",
  "decoding",
  "default",
  "defaultChecked",
  "defaultValue",
  "defer",
  "dir",
  "disabled",
  "disablePictureInPicture",
  "disableRemotePlayback",
  "download",
  "draggable",
  "encType",
  "enterKeyHint",
  "fetchPriority",
  "form",
  "formAction",
  "formEncType",
  "formMethod",
  "formNoValidate",
  "formTarget",
  "frameBorder",
  "headers",
  "height",
  "hidden",
  "high",
  "href",
  "hrefLang",
  "htmlFor",
  "httpEquiv",
  "id",
  "imageSizes",
  "imageSrcSet",
  "inert",
  "inputMode",
  "integrity",
  "is",
  "itemID",
  "itemProp",
  "itemRef",
  "itemScope",
  "itemType",
  "keyParams",
  "keyType",
  "kind",
  "label",
  "lang",
  "list",
  "loading",
  "loop",
  "low",
  "manifest",
  "marginHeight",
  "marginWidth",
  "max",
  "maxLength",
  "media",
  "mediaGroup",
  "method",
  "min",
  "minLength",
  "multiple",
  "muted",
  "name",
  "noModule",
  "noValidate",
  "nonce",
  "open",
  "optimum",
  "pattern",
  "placeholder",
  "playsInline",
  "poster",
  "preload",
  "profile",
  "radioGroup",
  "readOnly",
  "referrerPolicy",
  "rel",
  "required",
  "reversed",
  "role",
  "rows",
  "rowSpan",
  "sandbox",
  "scope",
  "scoped",
  "scrolling",
  "seamless",
  "selected",
  "shape",
  "size",
  "sizes",
  "slot",
  "span",
  "spellCheck",
  "src",
  "srcDoc",
  "srcLang",
  "srcSet",
  "start",
  "step",
  "style",
  "summary",
  "tabIndex",
  "target",
  "title",
  "translate",
  "type",
  "useMap",
  "value",
  "width",
  "wrap",
  // Event handlers
  "onAbort",
  "onAnimationEnd",
  "onAnimationIteration",
  "onAnimationStart",
  "onAuxClick",
  "onBeforeInput",
  "onBlur",
  "onChange",
  "onClick",
  "onCompositionEnd",
  "onCompositionStart",
  "onCompositionUpdate",
  "onContextMenu",
  "onCopy",
  "onCut",
  "onDoubleClick",
  "onDrag",
  "onDragEnd",
  "onDragEnter",
  "onDragExit",
  "onDragLeave",
  "onDragOver",
  "onDragStart",
  "onDrop",
  "onError",
  "onFocus",
  "onFocusIn",
  "onFocusOut",
  "onGotPointerCapture",
  "onInput",
  "onInvalid",
  "onKeyDown",
  "onKeyPress",
  "onKeyUp",
  "onLoad",
  "onLoadStart",
  "onLostPointerCapture",
  "onMouseDown",
  "onMouseEnter",
  "onMouseLeave",
  "onMouseMove",
  "onMouseOut",
  "onMouseOver",
  "onMouseUp",
  "onPaste",
  "onPause",
  "onPlay",
  "onPlaying",
  "onPointerCancel",
  "onPointerDown",
  "onPointerEnter",
  "onPointerLeave",
  "onPointerMove",
  "onPointerOut",
  "onPointerOver",
  "onPointerUp",
  "onProgress",
  "onRateChange",
  "onReset",
  "onResize",
  "onScroll",
  "onScrollEnd",
  "onSeeked",
  "onSeeking",
  "onSelect",
  "onStalled",
  "onSubmit",
  "onSuspend",
  "onTimeUpdate",
  "onToggle",
  "onTouchCancel",
  "onTouchEnd",
  "onTouchMove",
  "onTouchStart",
  "onTransitionEnd",
  "onVolumeChange",
  "onWaiting",
  "onWheel",
  // SVG attributes
  "accentHeight",
  "accumulate",
  "additive",
  "alignmentBaseline",
  "allowReorder",
  "alphabetic",
  "amplitude",
  "arabicForm",
  "ascent",
  "attributeName",
  "attributeType",
  "autoReverse",
  "azimuth",
  "baseFrequency",
  "baseProfile",
  "baselineShift",
  "bbox",
  "begin",
  "bias",
  "by",
  "calcMode",
  "capHeight",
  "clip",
  "clipPath",
  "clipPathUnits",
  "clipRule",
  "color",
  "colorInterpolation",
  "colorInterpolationFilters",
  "colorProfile",
  "contentScriptType",
  "contentStyleType",
  "cursor",
  "cx",
  "cy",
  "d",
  "decelerate",
  "descent",
  "diffuseConstant",
  "direction",
  "display",
  "divisor",
  "dominantBaseline",
  "dur",
  "dx",
  "dy",
  "edgeMode",
  "elevation",
  "enableBackground",
  "end",
  "exponent",
  "externalResourcesRequired",
  "fill",
  "fillOpacity",
  "fillRule",
  "filter",
  "filterRes",
  "filterUnits",
  "flood",
  "floodColor",
  "floodOpacity",
  "focusable",
  "font",
  "fontFamily",
  "fontSize",
  "fontSizeAdjust",
  "fontStretch",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "format",
  "from",
  "fx",
  "fy",
  "g1",
  "g2",
  "glyphName",
  "glyphOrientationHorizontal",
  "glyphOrientationVertical",
  "glyphRef",
  "gradientTransform",
  "gradientUnits",
  "hanging",
  "horizAdvX",
  "horizOriginX",
  "ideographic",
  "imageRendering",
  "in",
  "in2",
  "intercept",
  "k",
  "k1",
  "k2",
  "k3",
  "k4",
  "kernelMatrix",
  "kernelUnitLength",
  "kerning",
  "keyPoints",
  "keySplines",
  "keyTimes",
  "lengthAdjust",
  "letterSpacing",
  "lightingColor",
  "limitingConeAngle",
  "local",
  "markerEnd",
  "markerHeight",
  "markerMid",
  "markerStart",
  "markerUnits",
  "markerWidth",
  "mask",
  "maskContentUnits",
  "maskUnits",
  "mathematical",
  "mode",
  "numOctaves",
  "offset",
  "opacity",
  "operator",
  "order",
  "orient",
  "orientation",
  "origin",
  "overflow",
  "overlinePosition",
  "overlineThickness",
  "paintOrder",
  "panose1",
  "pathLength",
  "patternContentUnits",
  "patternTransform",
  "patternUnits",
  "pointerEvents",
  "points",
  "pointsAtX",
  "pointsAtY",
  "pointsAtZ",
  "preserveAlpha",
  "preserveAspectRatio",
  "primitiveUnits",
  "r",
  "radius",
  "refX",
  "refY",
  "renderingIntent",
  "repeatCount",
  "repeatDur",
  "requiredExtensions",
  "requiredFeatures",
  "restart",
  "result",
  "rotate",
  "rx",
  "ry",
  "scale",
  "seed",
  "shapeRendering",
  "slope",
  "spacing",
  "specularConstant",
  "specularExponent",
  "speed",
  "spreadMethod",
  "startOffset",
  "stdDeviation",
  "stemh",
  "stemv",
  "stitchTiles",
  "stopColor",
  "stopOpacity",
  "strikethroughPosition",
  "strikethroughThickness",
  "string",
  "stroke",
  "strokeDasharray",
  "strokeDashoffset",
  "strokeLinecap",
  "strokeLinejoin",
  "strokeMiterlimit",
  "strokeOpacity",
  "strokeWidth",
  "surfaceScale",
  "systemLanguage",
  "tableValues",
  "targetX",
  "targetY",
  "textAnchor",
  "textDecoration",
  "textLength",
  "textRendering",
  "to",
  "transform",
  "transformOrigin",
  "u1",
  "u2",
  "underlinePosition",
  "underlineThickness",
  "unicode",
  "unicodeBidi",
  "unicodeRange",
  "unitsPerEm",
  "vAlphabetic",
  "vHanging",
  "vIdeographic",
  "vMathematical",
  "values",
  "vectorEffect",
  "version",
  "vertAdvY",
  "vertOriginX",
  "vertOriginY",
  "viewBox",
  "viewTarget",
  "visibility",
  "widths",
  "wordSpacing",
  "writingMode",
  "x",
  "x1",
  "x2",
  "xChannelSelector",
  "xHeight",
  "xlinkActuate",
  "xlinkArcrole",
  "xlinkHref",
  "xlinkRole",
  "xlinkShow",
  "xlinkTitle",
  "xlinkType",
  "xmlBase",
  "xmlLang",
  "xmlSpace",
  "xmlns",
  "xmlnsXlink",
  "y",
  "y1",
  "y2",
  "yChannelSelector",
  "z",
  "zoomAndPan",
])

function isValidDOMProp(prop: string): boolean {
  if (prop === "children" || prop === "ref" || prop === "key") return true
  if (prop.startsWith("data-") || prop.startsWith("aria-")) return true
  return VALID_HTML_PROPS.has(prop)
}

// ── Types ────────────────────────────────────────────────────────────

interface DigitsCSSMeta {
  className: string
  extends?: string | React.ComponentType
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VarsMap = Record<string, (props: any) => unknown>

interface StyledOptions {
  cls: string
  vars?: VarsMap
}

// Marker type for compile-time metadata (matches Linaria's WYWEvalMeta pattern).
// _sc satisfies styled-components' StyledComponentBrand, making StyledComponent
// accepted as an Interpolation in styled-components templates.
type EvalMeta = {
  __digits_css: DigitsCSSMeta
  toString: () => string
  readonly _sc: symbol
}

// Matches Linaria's StyledComponent type exactly for proper tsgo inference.
// Using FunctionComponent (not ForwardRefExoticComponent) keeps the type simple
// enough for tsgo to resolve generic parameters in tagged template literals.

type StyledComponent<T = object> = EvalMeta &
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ([T] extends [React.FunctionComponent<any>]
    ? T
    : React.FunctionComponent<
        T & {
          as?: React.ElementType
        }
      >)

// CSS properties for template interpolations (simple recursive type, matches Linaria's CSSProperties)
type CSSProperties = {
  [key: string]: string | number | CSSProperties
}

// Static values allowed in tagged template interpolations
type StaticPlaceholder = string | number | CSSProperties | EvalMeta

// Matches @linaria/react's HtmlStyledTag exactly.
type HtmlStyledTag<TName extends keyof React.JSX.IntrinsicElements> = {
  <TAdditionalProps = Record<never, unknown>>(
    strings: TemplateStringsArray,
    ...exprs: Array<
      | StaticPlaceholder
      | ((
          props: React.JSX.IntrinsicElements[TName] & Omit<TAdditionalProps, never>
        ) => string | number)
    >
  ): StyledComponent<React.JSX.IntrinsicElements[TName] & TAdditionalProps>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attrs(obj: Record<string, any>): HtmlStyledTag<TName>
}

type ComponentStyledTag<TProps> = {
  <OwnProps = Record<never, unknown>>(
    strings: TemplateStringsArray,
    ...exprs: Array<
      StaticPlaceholder | ((props: TProps & Omit<OwnProps, never>) => string | number)
    >
  ): StyledComponent<OwnProps & TProps>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attrs(obj: Record<string, any>): ComponentStyledTag<TProps>
}

type StyledJSXIntrinsics = {
  readonly [P in keyof React.JSX.IntrinsicElements]: HtmlStyledTag<P>
}

type StyledProxy = StyledJSXIntrinsics & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <C extends React.ComponentType<any>>(
    component: C
  ): ComponentStyledTag<React.ComponentPropsWithRef<C>>
}

// ── Runtime implementation ───────────────────────────────────────────

function createStyledComponent(
  tag: string | React.ComponentType,
  options: StyledOptions | null,
  mockTag?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultAttrs?: Record<string, any>
): StyledComponent {
  const cls = options?.cls ?? (mockTag ? `mock-${mockTag}` : "mock-styled")
  const vars = options?.vars
  const attrsClassName = defaultAttrs?.className as string | undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = React.forwardRef<unknown, any>((props, ref) => {
    const { as: asProp, className: extraClassName, style: extraStyle, ...rest } = props

    const resolvedTag = asProp ?? tag
    const resolvedIsDom = typeof resolvedTag === "string"

    // Compute CSS custom property values from vars functions
    let varStyle: Record<string, string> | undefined
    if (vars) {
      varStyle = {}
      for (const [varName, fn] of Object.entries(vars)) {
        const val = fn(rest)
        if (val !== undefined && val !== null) {
          varStyle[varName] = String(val)
        }
      }
    }

    const mergedStyle = varStyle || extraStyle ? { ...varStyle, ...extraStyle } : undefined

    const mergedClassName = [cls, attrsClassName, extraClassName].filter(Boolean).join(" ")

    // Filter props for DOM elements
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let filteredProps: any
    if (resolvedIsDom) {
      filteredProps = {} as Record<string, unknown>
      for (const key of Object.keys(rest)) {
        if (key.startsWith("$")) continue
        if (!isValidDOMProp(key)) continue
        filteredProps[key] = rest[key]
      }
    } else {
      // For component tags (not DOM elements), forward all props including $-prefixed ones.
      // This matches styled-components' behavior: $ transient props are only filtered at
      // the DOM boundary. Component children are responsible for their own $ filtering.
      // This matters when wrapping unmigrated SC components that use $ props for styling.
      // Strip undefined values to match styled-components behavior — forwarding
      // role={undefined} (for example) would override a library's internal role="menu".
      filteredProps = {} as Record<string, unknown>
      for (const key of Object.keys(rest)) {
        if (rest[key] !== undefined) {
          filteredProps[key] = rest[key]
        }
      }
    }

    return React.createElement(resolvedTag, {
      ...filteredProps,
      ref,
      className: mergedClassName,
      style: mergedStyle,
    })
  })

  const displayName = cls.includes("_") ? cls.split("_")[0] : cls
  Component.displayName = displayName

  const styledComponent = Component as unknown as StyledComponent
  // eslint-disable-next-line no-underscore-dangle
  styledComponent.__digits_css = { className: cls, extends: tag }
  styledComponent.toString = () => `.${cls}`
  // Styled-components interop: when a digits-css component is interpolated as a
  // selector in an SC template, SC's flatten() checks isStyledComponent() which
  // requires `styledComponentId`. When an SC template wraps a digits-css component
  // via styled(Component), SC also reads `target` (the underlying element) and
  // `foldedComponentIds`. Setting these lets both paths work without warnings.
  const scInterop = styledComponent as unknown as {
    styledComponentId: string
    target: string | React.ComponentType
    foldedComponentIds: string
  }
  scInterop.styledComponentId = cls
  scInterop.target = tag
  scInterop.foldedComponentIds = ""
  // _sc satisfies styled-components' StyledComponentBrand type constraint.
  // eslint-disable-next-line no-underscore-dangle
  ;(styledComponent as unknown as { _sc: symbol })._sc = Symbol(cls)

  return styledComponent
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeStyledTag(tag: string | React.ComponentType, defaultAttrs?: Record<string, any>): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function styledTag(...args: any[]): StyledComponent {
    const first = args[0]
    // Production path: plugin-transformed options object
    if (first && typeof first === "object" && "cls" in first) {
      return createStyledComponent(tag, first as StyledOptions, undefined, defaultAttrs)
    }
    // Test path: tagged template literal (no plugin)
    if (first && typeof first === "object" && "raw" in first) {
      const mockTag =
        typeof tag === "string"
          ? tag
          : (tag as React.ComponentType & { displayName?: string }).displayName ||
            tag.name ||
            "Component"
      return createStyledComponent(tag, null, mockTag, defaultAttrs)
    }
    return createStyledComponent(tag, null, undefined, defaultAttrs)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styledTag.attrs = (attrsObj: Record<string, any>) =>
    makeStyledTag(tag, { ...defaultAttrs, ...attrsObj })
  return styledTag
}

const styled = new Proxy((() => {}) as unknown as StyledProxy, {
  get(_target, prop: string) {
    return makeStyledTag(prop)
  },
  apply(_target, _thisArg, args: unknown[]) {
    const component = args[0] as React.ComponentType
    return makeStyledTag(component)
  },
})

export default styled
export type { StyledComponent, StyledProxy, DigitsCSSMeta }

/**
 * A tagged template literal that interpolates values into a CSS string.
 * Use instead of styled-components' `css` when composing CSS fragments
 * for styledRuntime/digits-css templates.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function css<_P = object>(strings: TemplateStringsArray, ...values: unknown[]): string {
  return strings.reduce<string>((acc, str, i) => acc + str + (values[i] ?? ""), "")
}

/**
 * Build-time keyframes animation.
 *
 * Production path (digits-css plugin): the plugin replaces `keyframes\`...\``
 * with `keyframes({ name: "generatedName" })`, and this function simply returns
 * the pre-assigned animation name.
 *
 * Test/dev path (no plugin): falls back to a tagged template that generates a
 * stable mock name from the CSS content for use in tests.
 */
export function keyframes(
  arg: { name: string } | TemplateStringsArray,
  ...values: unknown[]
): string {
  // Production path: plugin has transformed to keyframes({ name: "..." })
  if (arg && !Array.isArray(arg) && typeof (arg as { name: string }).name === "string") {
    return (arg as { name: string }).name
  }
  // Test/dev path: tagged template literal — generate a mock name from content
  const strings = arg as TemplateStringsArray
  const body = strings.reduce<string>((acc, str, i) => acc + str + (values[i] ?? ""), "")
  // Simple djb2 hash for a stable, short name
  let h = 5381
  for (let i = 0; i < body.length; i++) {
    h = ((h << 5) + h + body.charCodeAt(i)) >>> 0
  }
  return `mock-kf-${h.toString(16).slice(0, 7)}`
}
